// ======================================================================
// FILE:
// src/modules/service_crm/tickets/components/CustomerFeedbackSection.js
//
// MODULE: Service CRM
// FEATURE: Customer Feedback Section
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import FormSectionCard
from "../../components/FormSectionCard";

import serviceTicketService
from "../../services/serviceTicketService";

export default function CustomerFeedbackSection({
  ticketId,
  ticketStatus,
  onSaved,
}) {

  // -------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------
  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [feedbackId, setFeedbackId] =
    useState(null);

  const [attachment, setAttachment] =
  useState(null);

const [uploading, setUploading] =
  useState(false);

  const [form, setForm] =
    useState({

      overall_rating: 0,

      comments: "",

      delivery_commitment_rating: 0,

      installation_commission_rating: 0,

      engineer_behavior_rating: 0,

      operation_awareness_rating: 0,

      overall_experience_rating: 0,
    });

  // -------------------------------------------------------------------
  // LOAD
  // -------------------------------------------------------------------
  useEffect(() => {

    loadFeedback();

  }, [ticketId]);

  // -------------------------------------------------------------------
  // LOAD FEEDBACK
  // -------------------------------------------------------------------
  async function loadFeedback() {

    try {

      setLoading(true);

      const data =
        await serviceTicketService
          .getFeedback(ticketId);

      if (data) {

        setFeedbackId(data.id); 
        setAttachment(
          data.attachment || null
        );

        setForm({

          overall_rating:
            data.overall_rating || 0,

          comments:
            data.comments || "",

          delivery_commitment_rating:
            data.delivery_commitment_rating || 0,

          installation_commission_rating:
            data.installation_commission_rating || 0,

          engineer_behavior_rating:
            data.engineer_behavior_rating || 0,

          operation_awareness_rating:
            data.operation_awareness_rating || 0,

          overall_experience_rating:
            data.overall_experience_rating || 0,
        });
      }

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);
    }
  }

  // -------------------------------------------------------------------
  // CHANGE
  // -------------------------------------------------------------------
  function setField(field, value) {

    setForm({

      ...form,

      [field]: value,
    });
  }

  // -------------------------------------------------------------------
  // CALCULATE %
  // -------------------------------------------------------------------
  const percentage = Math.round(

    (

      (
        Number(
          form.delivery_commitment_rating || 0
        )

        + Number(
          form.installation_commission_rating || 0
        )

        + Number(
          form.engineer_behavior_rating || 0
        )

        + Number(
          form.operation_awareness_rating || 0
        )

        + Number(
          form.overall_experience_rating || 0
        )
      )

      / 25

    ) * 100
  );

  // -------------------------------------------------------------------
  // SATISFACTION LABEL
  // -------------------------------------------------------------------
  function getSatisfactionLabel() {

    if (percentage >= 90) {
      return "Excellent";
    }

    if (percentage >= 75) {
      return "Good";
    }

    if (percentage >= 60) {
      return "Average";
    }

    return "Poor";
  }
  function handleFileChange(
    event
  ) {

    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowed = [

      "pdf",

      "jpg",
      "jpeg",
      "png",

      "zip",

    ];

    const ext =

      file.name
        .split(".")
        .pop()
        .toLowerCase();

    if (
      !allowed.includes(ext)
    ) {

      alert(

        "Allowed: PDF, JPG, JPEG, PNG, ZIP"

      );

      event.target.value = "";

      return;
    }

    if (

      file.size >

      512 * 1024

    ) {

      alert(
        "Maximum size is 512 KB"
      );

      event.target.value = "";

      return;
    }

    setAttachment(file);

  }
  async function
handleDownloadAttachment() {

  try {

    const blob =

      await serviceTicketService
        .downloadFeedbackAttachment(
          feedbackId
        );

    const url =

      window.URL.createObjectURL(
        blob
      );

    const a =
      document.createElement(
        "a"
      );

    a.href = url;

    a.download =

      attachment.file_name

      ||

      "attachment";

    document.body.appendChild(
      a
    );

    a.click();

    a.remove();

    window.URL
      .revokeObjectURL(
        url
      );

  } catch (err) {

    console.error(err);

    alert(
      "Failed to download attachment"
    );

  }

}
async function
handleDeleteAttachment() {

  try {

    if (

      !window.confirm(

        "Delete attachment?"

      )

    ) {

      return;

    }

    await serviceTicketService
      .deleteFeedbackAttachment(
        feedbackId
      );

    setAttachment(
      null
    );

    alert(
      "Attachment deleted successfully"
    );

  } catch (err) {

    console.error(err);

    alert(
      "Failed to delete attachment"
    );

  }

}
  
  // -------------------------------------------------------------------
  // SAVE
  // -------------------------------------------------------------------
  async function handleSave() {

    try {

      setSaving(true);

      if (feedbackId) {

        await serviceTicketService
          .updateFeedback(
            feedbackId,
            form
          );

        if (
          attachment instanceof File
        ) {

          await serviceTicketService
            .uploadFeedbackAttachment(
              feedbackId,
              attachment
            );

        }

        alert(
          "Feedback updated successfully"
        );

      } else {

        const created =

          await serviceTicketService
            .createFeedback(
              ticketId,
              form
            );

        const newFeedbackId =
          created.id;

        if (
          attachment instanceof File
        ) {

          await serviceTicketService
            .uploadFeedbackAttachment(
              newFeedbackId,
              attachment
            );

        }

        alert(
          "Feedback submitted successfully"
        );

      }
      await loadFeedback();

      if (onSaved) {
        onSaved();
      }

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to save feedback"
      );

    } finally {

      setSaving(false);
    }
  }

  // -------------------------------------------------------------------
  // STAR RENDER
  // -------------------------------------------------------------------
  function StarRating({
    value,
    onChange,
    disabled = false,
  }) {

    return (

      <div
        style={{
          display: "flex",
          gap: 6,
          fontSize: 24,
        }}
      >

        {[1, 2, 3, 4, 5]
          .map((star) => (

          <span
            key={star}

            onClick={() => {

              if (!disabled) {
                onChange(star);
              }
            }}

            style={{

              cursor:
                disabled
                  ? "default"
                  : "pointer",

              color:
                star <= value
                  ? "#ffc107"
                  : "#dee2e6",

              transition:
                "0.2s ease",
            }}
          >
            ★
          </span>
        ))}

      </div>
    );
  }

  // -------------------------------------------------------------------
  // READONLY
  // -------------------------------------------------------------------
  const readOnly =
    ticketStatus === "CLOSED";

  // -------------------------------------------------------------------
  // LOADING
  // -------------------------------------------------------------------
  if (loading) {

    return (

      <FormSectionCard
        title="Customer Feedback"
      >
        Loading feedback...
      </FormSectionCard>
    );
  }

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (

    <FormSectionCard
      title="Customer Feedback"
      collapsible
      defaultExpanded={true}
    >

      {/* ======================================================= */}
      {/* SUMMARY */}
      {/* ======================================================= */}

      <div
        style={{

          background: "#f8f9fa",

          borderRadius: 12,

          padding: 16,

          marginBottom: 24,

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          flexWrap: "wrap",

          gap: 16,
        }}
      >

        <div>

          <div
            style={{
              fontSize: 13,
              color: "#6c757d",
              marginBottom: 6,
            }}
          >
            Feedback Score
          </div>

          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            {percentage}%
          </div>

        </div>

        <div>

          <span
            style={{

              background:
                percentage >= 90
                  ? "#198754"
                  : percentage >= 75
                    ? "#0d6efd"
                    : percentage >= 60
                      ? "#fd7e14"
                      : "#dc3545",

              color: "#fff",

              padding:
                "8px 14px",

              borderRadius: 20,

              fontWeight: 600,
            }}
          >
            {getSatisfactionLabel()}
          </span>

        </div>

      </div>

      {/* ======================================================= */}
      {/* OVERALL */}
      {/* ======================================================= */}

      <div
        style={{
          marginBottom: 24,
        }}
      >

        <div
          style={{
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          Overall Rating
        </div>

        <StarRating
          value={form.overall_rating}

          onChange={(v) =>
            setField(
              "overall_rating",
              v
            )
          }

          disabled={readOnly}
        />

      </div>

      {/* ======================================================= */}
      {/* COMMENTS */}
      {/* ======================================================= */}

      <div
        style={{
          marginBottom: 24,
        }}
      >

        <label
          style={{
            fontWeight: 600,
            marginBottom: 8,
            display: "block",
          }}
        >
          Comments
        </label>

        <textarea
          rows="4"

          className="form-control"

          value={form.comments}

          disabled={readOnly}

          onChange={(e) =>
            setField(
              "comments",
              e.target.value
            )
          }
        />

      </div>

      {/* ======================================================= */}
      {/* DETAILED RATINGS */}
      {/* ======================================================= */}

      <div className="row">

        {[
          {
            label:
              "Delivery Commitment",
            field:
              "delivery_commitment_rating",
          },

          {
            label:
              "Installation & Commission",
            field:
              "installation_commission_rating",
          },

          {
            label:
              "Engineer Behaviour",
            field:
              "engineer_behavior_rating",
          },

          {
            label:
              "Operation Awareness",
            field:
              "operation_awareness_rating",
          },

          {
            label:
              "Overall Experience",
            field:
              "overall_experience_rating",
          },
        ].map((item) => (

          <div
            key={item.field}

            className="col-md-6 mb-4"
          >

            <div
              style={{
                fontWeight: 600,
                marginBottom: 10,
              }}
            >
              {item.label}
            </div>

            <StarRating

              value={
                form[item.field]
              }

              onChange={(v) =>
                setField(
                  item.field,
                  v
                )
              }

              disabled={readOnly}
            />

          </div>
        ))}

      </div>

      {/* ======================================================= */}
{/* ATTACHMENT */}
{/* ======================================================= */}

<div
  style={{
    marginTop: 24,
    marginBottom: 24,
  }}
>

  <label
    style={{
      fontWeight: 600,
      marginBottom: 10,
      display: "block",
    }}
  >
    Feedback Attachment
  </label>

  {!readOnly && (

    <input

      type="file"

      className="form-control"

      accept=".pdf,.jpg,.jpeg,.png,.zip"

      onChange={
        handleFileChange
      }

    />

  )}

  <small
    className="text-muted"
  >

    Allowed:
    PDF, JPG, JPEG, PNG, ZIP

    <br />

    Max Size:
    512 KB

  </small>

  {attachment && (

  <div
    className="mt-3"
  >

    <div>

      <b>

        {

          attachment.file_name

          ||

          attachment.name

        }

      </b>
      <div
  style={{
    marginTop: 10,
  }}
>

  <button

    type="button"

    className="btn btn-sm btn-outline-primary me-2"

    onClick={
      handleDownloadAttachment
    }
  >

    Download

  </button>

  {!readOnly && (

    <button

      type="button"

      className="btn btn-sm btn-outline-danger"

      onClick={
        handleDeleteAttachment
      }
    >

      Delete

    </button>

  )}

</div>

    </div>

    {
      attachment.file_size
      &&
      (
        <small
          className="text-muted"
        >

          {
            (
              attachment.file_size
              / 1024
            ).toFixed(2)
          }
          KB

        </small>
      )
    }

  </div>

)}

</div>
      {/* ======================================================= */}
      {/* ACTIONS */}
      {/* ======================================================= */}

      {!readOnly && (

        <div
          style={{
            marginTop: 12,
          }}
        >

          <button
            className="btn btn-primary"

            disabled={saving}

            onClick={handleSave}
          >

            {saving
              ? "Saving..."
              : feedbackId
                ? "Update Feedback"
                : "Submit Feedback"}

          </button>

        </div>
      )}

    </FormSectionCard>
  );
}